<!-- Copyright (c) 2021-2023 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details. -->
<script>
import NotificationMixin from '@forgerock/platform-shared/src/mixins/NotificationMixin';
import RestMixin from '@forgerock/platform-shared/src/mixins/RestMixin';
import TranslationMixin from '@forgerock/platform-shared/src/mixins/TranslationMixin';
import uuid from 'uuid/v4';
import { sortBy } from 'lodash';
import store from '@forgerock/platform-shared/src/store';
import i18n from '@/i18n';

export default {
  name: 'ThemeMixin',
  mixins: [
    NotificationMixin,
    RestMixin,
    TranslationMixin,
  ],
  data() {
    return {
      defaultThemeParams: {
        accountFooter: `<div class="d-flex flex-wrap justify-content-center py-4 w-100 text-center"><div class="d-flex align-items-center"><a href="${store.state.SharedStore.amBaseURL}/Privacy_Act_Statement.html" target="_blank" style="color:#0000FF" class="pr-2">Privacy Act Statement</a><span>|</span></div><div class="d-flex flex-wrap pl-2"><a href="mailto:icam.id.team@noaa.gov" target="_blank" style="color:#0000FF" class="pr-1">icam.id.team@noaa.gov</a><span class="pl-1">or call: (844) 662-2754 / 844 NOAA PKI</span></div></div>`,
        accountFooterEnabled: true,
        backgroundColor: '#f1f3f3',
        backgroundImage: '',
        buttonRounded: 5,
        dangerColor: '#db2207',
        favicon: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
        fontFamily: 'Open Sans',
        journeyCardBackgroundColor: '#ffffff',
        journeyCardHeaderBackgroundColor: '#ffffff',
        journeyCardShadow: 5,
        journeyCardBorderRadius: 7,
        journeyCardTextColor: '#323c46',
        journeyCardTitleColor: '#001743',
        journeyFloatingLabels: true,
        journeyFooter: `<div class="d-flex flex-wrap justify-content-center py-4 w-100 text-center"><div class="d-flex align-items-center"><a href="${store.state.SharedStore.amBaseURL}/Privacy_Act_Statement.html" target="_blank" style="color:#0000FF" class="pr-2">Privacy Act Statement</a><span>|</span></div><div class="d-flex flex-wrap pl-2"><a href="mailto:icam.id.team@noaa.gov" target="_blank" style="color:#0000FF" class="pr-1">icam.id.team@noaa.gov</a><span class="pl-1">or call: (844) 662-2754 / 844 NOAA PKI</span></div></div>`,
        journeyFooterEnabled: true,
        journeyHeader: `<div class="d-flex justify-content-center py-4 flex-grow-1 mt-5"><img src="${require('@forgerock/platform-shared/src/assets/images/noaa-login-banner.png')}" alt="Header Image" style="max-width:100%;height:auto" /></div>`,
        journeyHeaderEnabled: true,
        journeyInputBackgroundColor: '#e9f3f6',
        journeyInputBorderColor: '#a5aaaf',
        journeyInputLabelColor: '#5e6d82',
        journeyInputSelectColor: '#e4f4fd',
        journeyInputSelectHoverColor: '#f6f8fa',
        journeyInputTextColor: '#23282e',
        journeyTheaterMode: false,
        journeyJustifiedContent: '',
        journeyJustifiedContentEnabled: false,
        journeyLayout: 'card',
        journeySignInButtonPosition: 'flex-column',
        linkActiveColor: '#004067',
        linkColor: '#0070b3',
        linkedTrees: [],
        logo: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
        logoAltText: 'Logo',
        logoEnabled: false,
        logoHeight: '46',
        logoProfile: require('@forgerock/platform-shared/src/assets/images/noaa-logo-horizontal.png'),
        logoProfileAltText: 'Expanded Logo',
        logoProfileCollapsed: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
        logoProfileCollapsedAltText: 'Collapsed Logo',
        logoProfileHeight: '32',
        primaryColor: '#0085ca',
        primaryOffColor: '#0092de',
        profileBackgroundColor: '#ffffff',
        profileMenuHighlightColor: '#f6f8fa',
        profileMenuTextHighlightColor: '#455469',
        profileMenuHoverColor: '#f6f8fa',
        profileMenuHoverTextColor: '#455469',
        successColor: '#76bc21',
        textColor: '#ffffff',
        topBarBackgroundColor: '#ffffff',
        topBarBorderColor: '#e7eef4',
        topBarHeaderColor: '#23282e',
        topBarTextColor: '#69788b',
        secondaryColor: '#69788b',
        pageTitle: '#23282e',
        bodyText: '#23282e',
        switchBackgroundColor: '#c0c9d5',
        accountPageSections: {
          personalInformation: {
            enabled: true
          },
          accountSecurity: {
            enabled: true,
            subsections: {
              username: {
                enabled: true
              },
              password: {
                enabled: true
              },
              twoStepVerification: {
                enabled: true
              },
              securityQuestions: {
                enabled: false
              }
            }
          },
          social: {
            enabled: false
          },
          trustedDevices: {
            enabled: false
          },
          oauthApplications: {
            enabled: false
          },
          preferences: {
            enabled: false
          },
          consent: {
            enabled: false
          },
          accountControls: {
            enabled: false
          }
        },
        boldLinks: false
      },
      themesConfig: {
        realm: {
          "noaa-online" : [
            {
              accountFooter: `<div class="d-flex flex-wrap justify-content-center py-4 w-100 text-center"><div class="d-flex align-items-center"><a href="${store.state.SharedStore.amBaseURL}/Privacy_Act_Statement.html" target="_blank" style="color:#0000FF" class="pr-2">Privacy Act Statement</a><span>|</span></div><div class="d-flex flex-wrap pl-2"><a href="mailto:icam.id.team@noaa.gov" target="_blank" style="color:#0000FF" class="pr-1">icam.id.team@noaa.gov</a><span class="pl-1">or call: (844) 662-2754 / 844 NOAA PKI</span></div></div>`,
              accountFooterEnabled: true,
              backgroundColor: '#f1f3f3',
              backgroundImage: '',
              buttonRounded: 5,
              dangerColor: '#db2207',
              favicon: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              fontFamily: 'Open Sans',
              journeyCardBackgroundColor: '#ffffff',
              journeyCardHeaderBackgroundColor: '#ffffff',
              journeyCardShadow: 5,
              journeyCardBorderRadius: 7,
              journeyCardTextColor: '#323c46',
              journeyCardTitleColor: '#001743',
              journeyFloatingLabels: true,
              journeyFooter: `<div class="d-flex flex-wrap justify-content-center py-4 w-100 text-center"><div class="d-flex align-items-center"><a href="${store.state.SharedStore.amBaseURL}/Privacy_Act_Statement.html" target="_blank" style="color:#0000FF" class="pr-2">Privacy Act Statement</a><span>|</span></div><div class="d-flex flex-wrap pl-2"><a href="mailto:icam.id.team@noaa.gov" target="_blank" style="color:#0000FF" class="pr-1">icam.id.team@noaa.gov</a><span class="pl-1">or call: (844) 662-2754 / 844 NOAA PKI</span></div></div>`,
              journeyFooterEnabled: true,
              journeyHeader: `<div class="d-flex justify-content-center py-4 flex-grow-1 mt-5"><img src="${require('@forgerock/platform-shared/src/assets/images/noaa-login-banner.png')}" alt="Header Image" style="max-width:100%;height:auto" /></div>`,
              journeyHeaderEnabled: true,
              journeyInputBackgroundColor: '#e9f3f6',
              journeyInputBorderColor: '#a5aaaf',
              journeyInputLabelColor: '#5e6d82',
              journeyInputSelectColor: '#e4f4fd',
              journeyInputSelectHoverColor: '#f6f8fa',
              journeyInputTextColor: '#23282e',
              journeyTheaterMode: false,
              journeyJustifiedContent: '',
              journeyJustifiedContentEnabled: false,
              journeyLayout: 'card',
              journeySignInButtonPosition: 'flex-column',
              linkActiveColor: '#004067',
              linkColor: '#0070b3',
              linkedTrees: [
                'PlatformUpdatePassword',
                '2sv-enroll-tree'
              ],
              logo: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              logoAltText: 'Logo',
              logoEnabled: false,
              logoHeight: '46',
              logoProfile: require('@forgerock/platform-shared/src/assets/images/noaa-logo-horizontal.png'),
              logoProfileAltText: 'Expanded Logo',
              logoProfileCollapsed: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              logoProfileCollapsedAltText: 'Collapsed Logo',
              logoProfileHeight: '32',
              primaryColor: '#0085ca',
              primaryOffColor: '#0092de',
              profileBackgroundColor: '#ffffff',
              profileMenuHighlightColor: '#f6f8fa',
              profileMenuTextHighlightColor: '#455469',
              profileMenuHoverColor: '#f6f8fa',
              profileMenuHoverTextColor: '#455469',
              successColor: '#76bc21',
              textColor: '#ffffff',
              topBarBackgroundColor: '#ffffff',
              topBarBorderColor: '#e7eef4',
              topBarHeaderColor: '#23282e',
              topBarTextColor: '#69788b',
              _id: '4c105151-9bd0-42af-b0da-df5395905b5c',
              name: 'Password/MFA Theme',
              isDefault: false,
              secondaryColor: '#69788b',
              pageTitle: '#23282e',
              bodyText: '#23282e',
              switchBackgroundColor: '#c0c9d5',
              accountPageSections: {
                personalInformation: {
                  enabled: true
                },
                accountSecurity: {
                  enabled: true,
                  subsections: {
                    username: {
                      enabled: true
                    },
                    password: {
                      enabled: true
                    },
                    twoStepVerification: {
                      enabled: true
                    },
                    securityQuestions: {
                      enabled: false
                    }
                  }
                },
                social: {
                  enabled: false
                },
                trustedDevices: {
                  enabled: false
                },
                oauthApplications: {
                  enabled: false
                },
                preferences: {
                  enabled: false
                },
                consent: {
                  enabled: false
                },
                accountControls: {
                  enabled: false
                }
              },
              boldLinks: false
            },
            {
              accountFooter: `<div class="d-flex flex-wrap justify-content-center py-4 w-100 text-center"><div class="d-flex align-items-center"><a href="${store.state.SharedStore.amBaseURL}/Privacy_Act_Statement.html" target="_blank" style="color:#0000FF" class="pr-2">Privacy Act Statement</a><span>|</span></div><div class="d-flex flex-wrap pl-2"><a href="mailto:icam.id.team@noaa.gov" target="_blank" style="color:#0000FF" class="pr-1">icam.id.team@noaa.gov</a><span class="pl-1">or call: (844) 662-2754 / 844 NOAA PKI</span></div></div>`,
              accountFooterEnabled: true,
              backgroundColor: '#f1f3f3',
              backgroundImage: '',
              buttonRounded: 5,
              dangerColor: '#db2207',
              favicon: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              fontFamily: 'Open Sans',
              journeyCardBackgroundColor: '#ffffff',
              journeyCardHeaderBackgroundColor: '#ffffff',
              journeyCardShadow: 5,
              journeyCardBorderRadius: 7,
              journeyCardTextColor: '#323c46',
              journeyCardTitleColor: '#001743',
              journeyFloatingLabels: true,
              journeyFooter: '<div class=\"d-flex flex-wrap justify-content-center py-4 w-100 text-center\">\n  <span class=\"pr-1\">Having trouble logging in?</span>\n  <a href=\"https://icam.noaa.gov/help/login\" target=\"_blank\" style=\"color:#0000FF\">Click HERE for help!</a>\n</div>\n',
              journeyFooterEnabled: true,
              journeyHeader: `<div class="d-flex justify-content-center py-4 flex-grow-1 mt-5"><img src="${require('@forgerock/platform-shared/src/assets/images/noaa-login-banner.png')}" alt="Header Image" style="max-width:100%;height:auto" /></div>`,
              journeyHeaderEnabled: true,
              journeyInputBackgroundColor: '#e9f3f6',
              journeyInputBorderColor: '#a5aaaf',
              journeyInputLabelColor: '#5e6d82',
              journeyInputSelectColor: '#e4f4fd',
              journeyInputSelectHoverColor: '#f6f8fa',
              journeyInputTextColor: '#23282e',
              journeyTheaterMode: false,
              journeyJustifiedContent: '',
              journeyJustifiedContentEnabled: false,
              journeyLayout: 'card',
              journeySignInButtonPosition: 'flex-column',
              linkActiveColor: '#004067',
              linkColor: '#0070b3',
              linkedTrees: [
                'cac-meta-tree',
                '2sv-auth'
              ],
              logo: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              logoAltText: 'Logo',
              logoEnabled: false,
              logoHeight: '46',
              logoProfile: require('@forgerock/platform-shared/src/assets/images/noaa-logo-horizontal.png'),
              logoProfileAltText: 'Expanded Logo',
              logoProfileCollapsed: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              logoProfileCollapsedAltText: 'Collapsed Logo',
              logoProfileHeight: '32',
              primaryColor: '#0085ca',
              primaryOffColor: '#0092de',
              profileBackgroundColor: '#ffffff',
              profileMenuHighlightColor: '#f6f8fa',
              profileMenuTextHighlightColor: '#455469',
              profileMenuHoverColor: '#f6f8fa',
              profileMenuHoverTextColor: '#455469',
              successColor: '#76bc21',
              textColor: '#ffffff',
              topBarBackgroundColor: '#ffffff',
              topBarBorderColor: '#e7eef4',
              topBarHeaderColor: '#23282e',
              topBarTextColor: '#69788b',
              _id: '0de3d50b-80d0-4dba-a59d-53b3d896b91b',
              name: 'Login Theme',
              isDefault: true,
              secondaryColor: '#69788b',
              pageTitle: '#23282e',
              bodyText: '#23282e',
              switchBackgroundColor: '#c0c9d5',
              accountPageSections: {
                personalInformation: {
                  enabled: true
                },
                accountSecurity: {
                  enabled: true,
                  subsections: {
                    username: {
                      enabled: true
                    },
                    password: {
                      enabled: true
                    },
                    twoStepVerification: {
                      enabled: true
                    },
                    securityQuestions: {
                      enabled: false
                    }
                  }
                },
                social: {
                  enabled: false
                },
                trustedDevices: {
                  enabled: false
                },
                oauthApplications: {
                  enabled: false
                },
                preferences: {
                  enabled: false
                },
                consent: {
                  enabled: false
                },
                accountControls: {
                  enabled: false
                }
              },
              boldLinks: false
            }
          ],
          "cac": [
            {
              accountFooter: `<div class="d-flex flex-wrap justify-content-center py-4 w-100 text-center"><div class="d-flex align-items-center"><a href="${store.state.SharedStore.amBaseURL}/Privacy_Act_Statement.html" target="_blank" style="color:#0000FF" class="pr-2">Privacy Act Statement</a><span>|</span></div><div class="d-flex flex-wrap pl-2"><a href="mailto:icam.id.team@noaa.gov" target="_blank" style="color:#0000FF" class="pr-1">icam.id.team@noaa.gov</a><span class="pl-1">or call: (844) 662-2754 / 844 NOAA PKI</span></div></div>`,
              accountFooterEnabled: true,
              backgroundColor: '#f1f3f3',
              backgroundImage: '',
              buttonRounded: 5,
              dangerColor: '#db2207',
              favicon: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              fontFamily: 'Open Sans',
              journeyCardBackgroundColor: '#ffffff',
              journeyCardHeaderBackgroundColor: '#ffffff',
              journeyCardShadow: 5,
              journeyCardBorderRadius: 7,
              journeyCardTextColor: '#323c46',
              journeyCardTitleColor: '#001743',
              journeyFloatingLabels: true,
              journeyFooter: '<div class=\"d-flex flex-wrap justify-content-center py-4 w-100 text-center\">\n  <span class=\"pr-1\">Having trouble logging in?</span>\n  <a href=\"https://icam.noaa.gov/help/login\" target=\"_blank\" style=\"color:#0000FF\">Click HERE for help!</a>\n</div>\n',
              journeyFooterEnabled: true,
              journeyHeader: `<div class="d-flex justify-content-center py-4 flex-grow-1 mt-5"><img src="${require('@forgerock/platform-shared/src/assets/images/noaa-login-banner.png')}" alt="Header Image" style="max-width:100%;height:auto" /></div>`,
              journeyHeaderEnabled: true,
              journeyInputBackgroundColor: '#e9f3f6',
              journeyInputBorderColor: '#a5aaaf',
              journeyInputLabelColor: '#5e6d82',
              journeyInputSelectColor: '#e4f4fd',
              journeyInputSelectHoverColor: '#f6f8fa',
              journeyInputTextColor: '#23282e',
              journeyTheaterMode: false,
              journeyJustifiedContent: '',
              journeyJustifiedContentEnabled: false,
              journeyLayout: 'card',
              journeySignInButtonPosition: 'flex-column',
              linkActiveColor: '#004067',
              linkColor: '#0070b3',
              linkedTrees: [
                'cac-tree'
              ],
              logo: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              logoAltText: 'Logo',
              logoEnabled: false,
              logoHeight: '46',
              logoProfile: require('@forgerock/platform-shared/src/assets/images/noaa-logo-horizontal.png'),
              logoProfileAltText: 'Expanded Logo',
              logoProfileCollapsed: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
              logoProfileCollapsedAltText: 'Collapsed Logo',
              logoProfileHeight: '32',
              primaryColor: '#0085ca',
              primaryOffColor: '#0092de',
              profileBackgroundColor: '#ffffff',
              profileMenuHighlightColor: '#f6f8fa',
              profileMenuTextHighlightColor: '#455469',
              profileMenuHoverColor: '#f6f8fa',
              profileMenuHoverTextColor: '#455469',
              successColor: '#76bc21',
              textColor: '#ffffff',
              topBarBackgroundColor: '#ffffff',
              topBarBorderColor: '#e7eef4',
              topBarHeaderColor: '#23282e',
              topBarTextColor: '#69788b',
              _id: 'b587fe24-8c8c-4d6c-9e7d-a50c0fa8ddfe',
              name: 'CAC Login Theme',
              isDefault: true,
              secondaryColor: '#69788b',
              pageTitle: '#23282e',
              bodyText: '#23282e',
              switchBackgroundColor: '#c0c9d5',
              accountPageSections: {
                personalInformation: {
                  enabled: true
                },
                accountSecurity: {
                  enabled: true,
                  subsections: {
                    username: {
                      enabled: true
                    },
                    password: {
                      enabled: true
                    },
                    twoStepVerification: {
                      enabled: true
                    },
                    securityQuestions: {
                      enabled: false
                    }
                  }
                },
                social: {
                  enabled: false
                },
                trustedDevices: {
                  enabled: false
                },
                oauthApplications: {
                  enabled: false
                },
                preferences: {
                  enabled: false
                },
                consent: {
                  enabled: false
                },
                accountControls: {
                  enabled: false
                }
              }
            }
          ]
        }
      },
      favicon: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
      journeyFloatingLabels: true,
      journeyFooter: `<div class="d-flex flex-wrap justify-content-center py-4 w-100 text-center"><div class="d-flex align-items-center"><a href="${store.state.SharedStore.amBaseURL}/Privacy_Act_Statement.html" target="_blank" style="color:#0000FF" class="pr-2">Privacy Act Statement</a><span>|</span></div><div class="d-flex flex-wrap pl-2"><a href="mailto:icam.id.team@noaa.gov" target="_blank" style="color:#0000FF" class="pr-1">icam.id.team@noaa.gov</a><span class="pl-1">or call: (844) 662-2754 / 844 NOAA PKI</span></div></div>`,
      journeyFooterEnabled: true,
      journeyHeader: `<div class="d-flex justify-content-center py-4 flex-grow-1 mt-5"><img src="${require('@forgerock/platform-shared/src/assets/images/noaa-login-banner.png')}" alt="Header Image" style="max-width:100%;height:auto" /></div>`,
      journeyHeaderEnabled: true,
      journeyTheaterMode: false,
      journeyJustifiedContent: '',
      journeyJustifiedContentEnabled: false,
      journeyLayout: 'card',
      journeySignInButtonPosition: 'flex-column',
      logoHeight: '46',
      logoProfileHeight: '32',
      logo: require('@forgerock/platform-shared/src/assets/images/noaa-logo.png'),
      logoAltText: 'Logo',
      logoEnabled: false,
      theme: null,
      realmThemeNames: [],
    };
  },
  computed: {
    localizedFavicon() {
      return this.getLocalizedString(this.favicon, i18n.locale, i18n.fallbackLocale);
    },
  },
  methods: {
    /**
     * Sets up the current theme based on whether we are using tree, org, existing tree etc.
     * @param {string} realm - The current realm
     * @param {object} themeOptions - the tree/themeId/org/etc data we are using to find the correct theme
     */
    setTheme(realm, themeOptions) {
      const themeResults = this.themesConfig.realm;
      let cleanRealm = realm === 'root' ? '/' : realm;

      if (themeResults[cleanRealm] === undefined && cleanRealm.charAt(0) === '/') {
        cleanRealm = cleanRealm.substring(1);
      }

      const storedRealm = localStorage.getItem('cleanRealm');

      if (!storedRealm || storedRealm !== cleanRealm) {
        localStorage.setItem('cleanRealm', cleanRealm);
        themeOptions.themeId = '';
        if (this.$store.state.SharedStore.webStorageAvailable) {
          localStorage.removeItem('theme-id');
        }
      }

      let theme = themeResults[cleanRealm];

      if (!theme || !theme.length) {
        this.theme = this.defaultThemeParams;
        return Promise.resolve();
      }

      if (Array.isArray(theme)) {
        if (themeOptions.nodeThemeId) {
          themeOptions.themeId = themeOptions.nodeThemeId;
          if (this.$store.state.SharedStore.webStorageAvailable) {
            localStorage.setItem('theme-id', themeOptions.nodeThemeId);
          }
        } else if (themeOptions.treeId) {
          const treeTheme = theme.find(realmTheme => 
            realmTheme.linkedTrees && realmTheme.linkedTrees.includes(themeOptions.treeId)
          );
          if (treeTheme) {
            themeOptions.themeId = treeTheme._id;
            if (this.$store.state.SharedStore.webStorageAvailable) {
              localStorage.setItem('theme-id', treeTheme._id);
            }
          }
        }

        theme = themeOptions.themeId 
          ? theme.find(realmTheme => realmTheme._id === themeOptions.themeId || realmTheme.name === themeOptions.themeId)
          : theme.find(realmTheme => realmTheme.isDefault);
      }

      if (!theme) {
        this.theme = this.defaultThemeParams;
        this.favicon = 'favicon.ico';
        return Promise.resolve();
      }

      this.theme = theme;
      this.favicon = theme.favicon;
      this.journeyFloatingLabels = theme.journeyFloatingLabels;
      this.journeyFooter = theme.journeyFooter;
      this.journeyFooterEnabled = theme.journeyFooterEnabled;
      this.journeyHeader = theme.journeyHeader;
      this.journeyHeaderEnabled = theme.journeyHeaderEnabled;
      this.journeyTheaterMode = theme.journeyTheaterMode;
      this.journeyJustifiedContent = theme.journeyJustifiedContent;
      this.journeyJustifiedContentEnabled = theme.journeyJustifiedContentEnabled;
      this.journeyLayout = theme.journeyLayout;
      this.journeySignInButtonPosition = theme.journeySignInButtonPosition;
      this.logo = theme.logo;
      this.logoAltText = theme.logoAltText;
      this.logoEnabled = theme.logoEnabled;
      this.logoHeight = theme.logoHeight;
      this.logoProfileHeight = theme.logoProfileHeight;

      return Promise.resolve();
    },
  },
};
</script>
